(() => {
  if (customElements.get('ab-pd2-community')) return;
  customElements.define('ab-pd2-community', class extends HTMLElement {
    connectedCallback() {
      this.initFrame = requestAnimationFrame(() => this.initialize());
    }
    initialize() {
      if (!this.isConnected || this.controller) return;
      this.track = this.querySelector('[data-ugc-track]');
      this.cards = [...this.querySelectorAll('[data-ugc-card]')];
      this.dialog = this.querySelector('[data-ugc-dialog]');
      this.video = this.querySelector('[data-ugc-video]');
      this.status = this.querySelector('[data-ugc-status]');
      this.dots = this.querySelector('[data-ugc-dots]');
      if (!this.track || !this.dialog || !this.video) return;
      this.controller = new AbortController();
      this.generation = 0;
      const signal = this.controller.signal;
      this.querySelectorAll('[data-ugc-open]').forEach(button => {
        button.addEventListener('click', () => this.openVideo(button), { signal });
      });
      this.querySelector('[data-ugc-close]').addEventListener('click', () => this.closeVideo(), { signal });
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.closeVideo(); }, { signal });
      this.dialog.addEventListener('close', () => { if (!this.dialog.open) this.releaseVideo(); }, { signal });
      this.dialog.addEventListener('click', event => {
        const r = this.dialog.getBoundingClientRect();
        if (event.target === this.dialog && (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom)) this.closeVideo();
      }, { signal });
      this.video.addEventListener('error', () => {
        if (this.video.getAttribute('src')) this.status.textContent = 'Unable to load this video. Please try again.';
      }, { signal });
      this.video.addEventListener('playing', () => { this.status.textContent = ''; }, { signal });
      this.track.addEventListener('scroll', () => this.sync(), { passive: true, signal });
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault(); this.go(this.current + (event.key === 'ArrowLeft' ? -1 : 1));
      }, { signal });
      this.querySelector('[data-ugc-prev]').addEventListener('click', () => this.go(this.current - 1), { signal });
      this.querySelector('[data-ugc-next]').addEventListener('click', () => this.go(this.current + 1), { signal });
      this.dots.addEventListener('click', event => {
        const dot = event.target.closest('[data-page]');
        if (dot) this.go(Number(dot.dataset.page));
      }, { signal });
      this.addEventListener('shopify:block:select', event => {
        const card = event.target.closest('[data-ugc-card]');
        if (card) this.track.scrollTo({ left: this.cardLeft(card), behavior: 'instant' });
      }, { signal });
      document.addEventListener('pd2:sound-play', event => { if (event.detail !== this) this.closeVideo(); }, { signal });
      document.addEventListener('visibilitychange', () => { if (document.hidden) this.video.pause(); }, { signal });
      window.addEventListener('pagehide', () => this.closeVideo(), { signal });
      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(() => this.measure());
        this.observer.observe(this.track);
      } else window.addEventListener('resize', () => this.measure(), { signal });
      this.measure();
      this.setAttribute('data-ready', '');
    }
    cardLeft(card) {
      return card.getBoundingClientRect().left - this.track.getBoundingClientRect().left + this.track.scrollLeft;
    }
    measure() {
      const max = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      this.positions = [];
      this.cards.forEach(card => {
        const left = Math.min(max, Math.max(0, this.cardLeft(card)));
        if (!this.positions.length || Math.abs(left - this.positions[this.positions.length - 1]) > 2) this.positions.push(left);
      });
      this.dots.replaceChildren(...this.positions.map((_, i) => {
        const button = document.createElement('button');
        button.type = 'button'; button.dataset.page = String(i);
        button.setAttribute('aria-label', `Video page ${i + 1}`);
        return button;
      }));
      this.querySelector('[data-ugc-controls]').hidden = max < 2;
      this.sync();
    }
    sync() {
      if (!this.positions?.length) return;
      this.current = this.positions.reduce((best, left, index) => Math.abs(left - this.track.scrollLeft) < Math.abs(this.positions[best] - this.track.scrollLeft) ? index : best, 0);
      [...this.dots.children].forEach((dot, i) => dot.setAttribute('aria-current', String(i === this.current)));
      this.querySelector('[data-ugc-prev]').disabled = this.current === 0;
      this.querySelector('[data-ugc-next]').disabled = this.current === this.positions.length - 1;
    }
    go(index) {
      if (!this.positions.length) return;
      const position = this.positions[Math.max(0, Math.min(index, this.positions.length - 1))];
      this.track.scrollTo({ left: position, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }
    async openVideo(button) {
      if (!button.dataset.src) return;
      this.releaseVideo();
      document.dispatchEvent(new CustomEvent('pd2:sound-play', { detail: this }));
      this.querySelector('[data-ugc-title]').textContent = button.dataset.title;
      this.video.src = button.dataset.src;
      this.status.textContent = '';
      if (!this.dialog.open) this.dialog.showModal();
      const generation = this.generation;
      try { await this.video.play(); }
      catch (error) {
        if (generation === this.generation && this.dialog.open) this.status.textContent = 'Press play in the video controls. If it does not load, close and try again.';
      }
    }
    releaseVideo() {
      this.generation++;
      this.video.pause();
      this.video.removeAttribute('src');
      this.video.load();
    }
    closeVideo() {
      if (this.dialog.open) this.dialog.close();
      this.releaseVideo();
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.initFrame);
      if (this.controller) {
        this.closeVideo(); this.controller.abort(); this.observer?.disconnect(); this.controller = null;
      }
      this.removeAttribute('data-ready');
    }
  });
})();
